import { useEffect, useState } from 'react';
import axios from 'axios';
import { ChevronLeftIcon, ChevronRightIcon } from '@radix-ui/react-icons';

import { ErrorPage } from '../../../ErrorPage';
import { BaseLoading } from '../Loading';
import { PdfPanel } from '../components/PdfPanel';
import { PoweredByQITech } from '../components/PoweredByQITech';
import { CheckboxField } from '../components/CheckboxField';
import { useThemeContext } from '../../../../contexts/ThemeContext';
import { getOrCreateBrowserKey } from '../../../../utils/fingerprint_utils';
import { resolveColor } from '../../theme/utils/resolveColor';
import * as S from './styles';

const MAX_DOCUMENTS_WITHOUT_SIGN_ALL = 10;

interface DocumentDisplayProps {
    signData: any;
    setSignerStep: (step: string) => void;
    setSignData: (data: any) => void;
}

interface SignerDocument {
    key?: string;
    name: string;
    url: string | null;
}

type SubStep = 'documents' | 'confirmation';

export const BaseDocumentDisplay = ({
    signData,
    setSignerStep,
    setSignData,
}: DocumentDisplayProps) => {
    const { clientThemeSettings } = useThemeContext();
    const primary = resolveColor('primary', clientThemeSettings);
    const text = resolveColor('text', clientThemeSettings);
    const initialError = { has_error: false, status: 0 };

    const [error, setError] = useState<{ has_error: boolean; status: number }>(initialError);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [documents, setDocuments] = useState<SignerDocument[]>([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [furthestIndex, setFurthestIndex] = useState(0);
    const [ready, setReady] = useState<boolean[]>([]);
    const [subStep, setSubStep] = useState<SubStep>('documents');
    const [accepted, setAccepted] = useState<boolean[]>([]);

    const shouldReinforceAcceptance = Boolean(signData.reinforce_documents_acceptance);
    const shouldAcceptEachDocument = shouldReinforceAcceptance && documents.length > 1;
    const canSignAll = documents.length > MAX_DOCUMENTS_WITHOUT_SIGN_ALL;
    const isLastDocument = currentIndex === documents.length - 1;
    const currentDocument = documents[currentIndex];
    const currentDocumentReady = ready[currentIndex] ?? false;
    const canGoPrevious = currentIndex > 0;
    const canGoNext = !isLastDocument && (canSignAll || currentIndex < furthestIndex);

    const handleRequestError = (err: any) => {
        const status = [401, 403, 409].includes(err.response?.status) ? err.response.status : 500;
        setError({ has_error: true, status });
    };

    useEffect(() => {
        const config = { headers: { Authorization: signData.signer_token } };

        axios
            .get('/signer/documents', config)
            .then((response) => {
                const docs: SignerDocument[] = [...(response.data.documents || [])].reverse();
                setDocuments(docs);
                setAccepted(docs.map(() => false));
                setReady(docs.map(() => false));
                setSignData((prevData: any) => ({ ...prevData, documents: docs }));
            })
            .catch(handleRequestError)
            .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [signData.signer_token]);

    const markCurrentReady = () => {
        setReady((prev) => prev.map((value, i) => (i === currentIndex ? true : value)));
    };

    const onDocumentLoad = (numPages: number) => {
        if (!shouldReinforceAcceptance || numPages <= 1) markCurrentReady();
    };

    const onPageChange = (currentPage: number, numPages: number) => {
        if (shouldReinforceAcceptance && currentPage + 1 >= numPages) markCurrentReady();
    };

    const goToDocument = (index: number) => {
        setCurrentIndex(index);
        setFurthestIndex((prev) => Math.max(prev, index));
    };

    const toggleAccepted = (index: number, checked: boolean) => {
        setAccepted((prev) => prev.map((value, i) => (i === index ? checked : value)));
    };

    const toggleAllAccepted = (checked: boolean) => {
        setAccepted((prev) => prev.map(() => checked));
    };

    const allAccepted = accepted.length > 0 && accepted.every(Boolean);

    const onSubmit = async () => {
        let browserKey: string | null = null;
        try {
            browserKey = await getOrCreateBrowserKey();
        } catch (_) {}

        const payload = {
            event_date: new Date().toISOString(),
            step: 'document_display',
            device_data: { device_key: browserKey },
        };
        const config = { headers: { Authorization: signData.signer_token } };

        setSubmitting(true);
        axios
            .patch('/signer', payload, config)
            .then((response) => {
                setSignerStep(response.data.next_step);
                setSignData({ ...signData, documents, ...response.data });
            })
            .catch(handleRequestError)
            .finally(() => setSubmitting(false));
    };

    const onSignCurrent = () => {
        if (!currentDocumentReady) return;

        if (!isLastDocument) {
            goToDocument(currentIndex + 1);
            return;
        }

        if (shouldAcceptEachDocument) {
            setSubStep('confirmation');
            return;
        }

        onSubmit();
    };

    const onSignAll = () => {
        setSubStep('confirmation');
    };

    const onConfirm = () => {
        if (!allAccepted) return;
        onSubmit();
    };

    if (loading || submitting) return <BaseLoading />;

    const renderHeader = () => (
        <S.LogoSection>
            {clientThemeSettings?.logo_url && (
                <S.LogoImage src={clientThemeSettings.logo_url} alt="logo" />
            )}
        </S.LogoSection>
    );

    const renderError = () => error.has_error && (
        <ErrorPage
            error={error}
            redirectToLogin={() => setSignerStep('login')}
            resetError={() => setError(initialError)}
            clientThemeSettings={clientThemeSettings}
        />
    );

    if (subStep === 'confirmation') {
        return (
            <S.PageWrapper>
                {renderHeader()}

                <S.ScrollContent>
                    <S.IntroSection>
                        <S.Title $color={primary}>Confirmação de ciência</S.Title>
                        <S.Subtitle>
                            {shouldAcceptEachDocument
                                ? 'Precisamos da sua confirmação sobre a contratação de múltiplos produtos na mesma jornada de assinatura. Marque todos os documentos para continuar.'
                                : `Você está prestes a assinar ${documents.length} documentos de uma só vez. Confirme que leu e concorda com o conteúdo de todos eles.`}
                        </S.Subtitle>
                    </S.IntroSection>

                    {!shouldAcceptEachDocument && (
                        <S.DocumentNameList>
                            {documents.map((doc, index) => (
                                <S.DocumentNameItem key={doc.key ?? `${doc.name}-${index}`}>
                                    {doc.name}
                                </S.DocumentNameItem>
                            ))}
                        </S.DocumentNameList>
                    )}

                    {shouldAcceptEachDocument && (
                        <S.CheckboxGroup>
                            {documents.map((doc, index) => (
                                <CheckboxField
                                    key={doc.key ?? `${doc.name}-${index}`}
                                    id={`base-document-acceptance-${index}`}
                                    checked={accepted[index] ?? false}
                                    onChange={(e) => toggleAccepted(index, e.target.checked)}
                                    checkboxColor={primary}
                                    textColor={text}
                                >
                                    Li e concordo com o documento <strong>{doc.name}</strong>.
                                </CheckboxField>
                            ))}
                        </S.CheckboxGroup>
                    )}
                </S.ScrollContent>

                {renderError()}

                <S.Footer>
                    {!shouldAcceptEachDocument && (
                        <S.FooterCheckbox>
                            <CheckboxField
                                id="base-document-acceptance"
                                checked={allAccepted}
                                onChange={(e) => toggleAllAccepted(e.target.checked)}
                                checkboxColor={primary}
                                textColor={text}
                            >
                                Li e concordo com o conteúdo de todos os documentos.
                            </CheckboxField>
                        </S.FooterCheckbox>
                    )}
                    <S.SubmitButton
                        $backgroundColor={primary}
                        $disabled={!allAccepted}
                        disabled={!allAccepted}
                        onClick={onConfirm}
                    >
                        Continuar
                        <ChevronRightIcon width={24} height={24} />
                    </S.SubmitButton>
                    <S.OutlineButton $color={primary} onClick={() => setSubStep('documents')}>
                        <ChevronLeftIcon width={20} height={20} />
                        Voltar aos documentos
                    </S.OutlineButton>
                    <PoweredByQITech />
                </S.Footer>
            </S.PageWrapper>
        );
    }

    return (
        <S.PageWrapper>
            {renderHeader()}

            <S.ViewerContainer>
                {currentDocument?.url ? (
                    <PdfPanel
                        key={currentDocument.key ?? currentIndex}
                        url={currentDocument.url}
                        title={currentDocument.name}
                        onDocumentLoad={onDocumentLoad}
                        onPageChange={onPageChange}
                        onFallbackLoad={markCurrentReady}
                    />
                ) : (
                    <S.FallbackBlock>Preparando seu documento...</S.FallbackBlock>
                )}
            </S.ViewerContainer>

            {renderError()}

            <S.Footer>
                <S.DocumentInfo>
                    <S.CounterRow>
                        <S.NavButton
                            type="button"
                            $color={primary}
                            disabled={!canGoPrevious}
                            onClick={() => goToDocument(currentIndex - 1)}
                            aria-label="Documento anterior"
                        >
                            <ChevronLeftIcon width={20} height={20} />
                        </S.NavButton>
                        <S.Counter $color={primary}>
                            Documento {currentIndex + 1} de {documents.length}
                        </S.Counter>
                        <S.NavButton
                            type="button"
                            $color={primary}
                            disabled={!canGoNext}
                            onClick={() => goToDocument(currentIndex + 1)}
                            aria-label="Próximo documento"
                        >
                            <ChevronRightIcon width={20} height={20} />
                        </S.NavButton>
                    </S.CounterRow>
                    {currentDocument && <S.DocumentName>{currentDocument.name}</S.DocumentName>}
                </S.DocumentInfo>
                {canSignAll ? (
                    <S.SubmitButton
                        $backgroundColor={primary}
                        $disabled={false}
                        onClick={onSignAll}
                    >
                        Assinar todos os {documents.length} documentos
                        <ChevronRightIcon width={24} height={24} />
                    </S.SubmitButton>
                ) : (
                    <>
                        {shouldReinforceAcceptance && !currentDocumentReady && (
                            <S.HintText>
                                O botão será habilitado ao ler a <strong>última página</strong>.
                            </S.HintText>
                        )}
                        <S.SubmitButton
                            $backgroundColor={primary}
                            $disabled={!currentDocumentReady}
                            disabled={!currentDocumentReady}
                            onClick={onSignCurrent}
                        >
                            {isLastDocument ? 'Assinar e continuar' : 'Assinar documento'}
                            <ChevronRightIcon width={24} height={24} />
                        </S.SubmitButton>
                    </>
                )}
                <PoweredByQITech />
            </S.Footer>
        </S.PageWrapper>
    );
};
